import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { StoreMessage } from './types';
import Logger from '../../utils/Logger';
import { useScrollToListTop } from '../../hooks/useScrollToListTop';

interface MessageHistoryProps {
  storeId?: string;
  refreshTrigger?: number; // Change this to trigger refresh
}

/**
 * Message History Component
 *
 * Displays a table of sent messages with filtering and pagination
 * - Shows message details (title, content, type, etc.)
 * - Displays creator and send time
 * - Shows push notification status
 */
const MessageHistory: React.FC<MessageHistoryProps> = ({ storeId, refreshTrigger }) => {
  const [messages, setMessages] = useState<StoreMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const listTopRef = useScrollToListTop(page);
  const [totalCount, setTotalCount] = useState(0);
  const notify = useNotify();

  /**
   * Fetch messages from API (only expired/closed messages)
   */
  const fetchMessages = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(pageSize));
      params.append('status', 'expired'); // Only show closed messages

      if (storeId) {
        params.append('storeId', storeId);
      }

      const { data: json } = await authInstance.get(`/admin/store-messages?${params.toString()}`);

      setMessages(json.messages || []);
      setTotalCount(json.total || 0);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load messages';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      Logger.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, storeId, refreshTrigger]);

  const columns: GridColDef[] = [
    {
      field: 'sentAt',
      headerName: 'Sent At',
      width: 160,
      renderCell: (params: any) => {
        if (!params.value) return 'Not sent';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'expiresAt',
      headerName: 'Closed At',
      width: 160,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString();
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 180,
    },
    {
      field: 'content',
      headerName: 'Message',
      width: 300,
      renderCell: (params: any) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word', py: 1 }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: 'messageType',
      headerName: 'Type',
      width: 120,
      renderCell: (params: any) => {
        return params.value === 'global' ? 'Global' : 'Store';
      },
    },
    {
      field: 'targetStore',
      headerName: 'Store',
      width: 180,
      renderCell: (params: any) => {
        return params.row.targetStore?.name || '-';
      },
    },
    {
      field: 'sendPushNotification',
      headerName: 'Push Sent',
      width: 100,
      renderCell: (params: any) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'creator',
      headerName: 'Sent By',
      width: 180,
      renderCell: (params: any) => {
        const creator = params.row.creator;
        return creator ? creator.email : 'Unknown';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: any) => {
        const status = params.value as string;
        return status.charAt(0).toUpperCase() + status.slice(1);
      },
    },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          📜 Message History (Closed Messages)
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          This shows messages that have been closed or expired. Active messages are shown above.
        </Alert>

        <Box ref={listTopRef} sx={{ height: 600, width: '100%', mt: 2 }}>
          <DataGrid
            rows={messages}
            columns={columns}
            loading={loading}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50]}
            rowCount={totalCount}
            paginationMode="server"
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default MessageHistory;
